import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { SolverInfo, SolverWorkingInfo } from "@app/models/dto/solver-issue";
import { BehaviorSubject, firstValueFrom, Observable } from "rxjs";
import { SolverStatus } from "@app/models/dto/solver-status.enum";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { SolutionState } from "@app/models/dto/solution-state";
import { RxStompService } from "@app/services/webstomp/rx-stomp.service";
import { WhatIfStatus } from "@app/services/rest/whatIfStatus";
import { ResourceAvailabilityDto } from "@app/models/dto/resource-availability-dto";

@Injectable({
  providedIn: "root"
})
export class SolverService {
  private clientConfigService = inject(ClientConfigService);
  private http = inject(HttpClient);
  private stompService = inject(RxStompService);

  constructor() {
    const stompService = this.stompService;

    this._updateStream = new BehaviorSubject<SolverInfo>({
      status: SolverStatus.SLEEPING,
      score: { initScore: 0, hardScores: [0, 0], softScores: [] },
      name: "Loading...",
      startedDateTime: null,
      lastRunDateTime: null,
      changeEvent: null,
      lastExplain: null,
      issues: []
    });
    stompService.watch("/topic/status").subscribe((message) => {
      console.log("status", message);
      this._updateStream.next(JSON.parse(message.body) as SolverInfo);
    });

    this._whatIfStream = new BehaviorSubject<WhatIfStatus>({
      activeMachineCount: 0,
      totalRequests: 0,
      completedRequests: 0,
      skippedRequests: 0,
      cancelledRequests: 0,
      currentRequests: 0
    });
    stompService.watch("/topic/stats/whatIf").subscribe((message) => {
      console.log("whatIfStatus", message);
      let parsed = JSON.parse(message.body) as WhatIfStatus;
      console.log("whatIfStatusParsed", parsed);
      this._whatIfStream.next(parsed);
    });

    this._workingStatusStream = new BehaviorSubject<SolverWorkingInfo>({
      score: { initScore: 0, hardScores: [0, 0], softScores: [] },
      hardTicketIssues: [],
      unplannableTickets: []
    });
    stompService.watch("/topic/workingScore").subscribe((message) => {
      console.log("workingScore", message);
      let parsed = JSON.parse(message.body) as SolverWorkingInfo;
      console.log("workingScoreParsed", parsed);
      this._workingStatusStream.next(parsed);
    });
  }

  protected _updateStream: BehaviorSubject<SolverInfo>;

  get updateStream(): Observable<SolverInfo> {
    return this._updateStream.asObservable();
  }

  protected _whatIfStream: BehaviorSubject<WhatIfStatus>;

  get whatIfStream(): Observable<WhatIfStatus> {
    return this._whatIfStream.asObservable();
  }

  protected _workingStatusStream: BehaviorSubject<SolverWorkingInfo>;

  get workingStatusStream(): Observable<SolverWorkingInfo> {
    return this._workingStatusStream.asObservable();
  }

  public async start(): Promise<any> {
    const config = await this.clientConfigService.config;
    return await firstValueFrom(
      this.http.post(config.serviceUrl + "/api/v1/solver/start", {}, {})
    );
  }

  public async restart(): Promise<any> {
    const config = await this.clientConfigService.config;
    return await firstValueFrom(
      this.http.post(config.serviceUrl + "/api/v1/solver/restart", {}, {})
    );
  }

  public async reloadPostcode(): Promise<any> {
    const config = await this.clientConfigService.config;
    return await firstValueFrom(
      this.http.post(
        config.serviceUrl + "/api/v1/solver/reloadPostcodes",
        {},
        {}
      )
    );
  }

  public async stop(): Promise<any> {
    const config = await this.clientConfigService.config;
    return await firstValueFrom(
      this.http.post(config.serviceUrl + "/api/v1/solver/stop", {}, {})
    );
  }

  public async ticketState(): Promise<SolutionState> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/solver/tickets", {
        withCredentials: true
      })
    )) as unknown as SolutionState;
  }

  public async diaryState(): Promise<SolutionState> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/solver/diary", {
        withCredentials: true
      })
    )) as unknown as SolutionState;
  }

  public async status(): Promise<SolverInfo> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/solver/status", {
        withCredentials: true
      })
    )) as unknown as SolverInfo;
  }

  public async explainBestScore(): Promise<string> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/solver/explain", {
        withCredentials: true,
        responseType: "text"
      })
    )) as unknown as string;
  }

  /**
   * Check availability details including effective skills and zones
   * @param availabilityId The ID of the resource availability to check
   * @returns ResourceAvailabilityDto with skills and zones arrays
   */
  public async checkAvailability(
    availabilityId: number
  ): Promise<ResourceAvailabilityDto> {
    const config = await this.clientConfigService.config;
    return (await firstValueFrom(
      this.http.get(`${config.serviceUrl}/api/v1/solver/checkAvailability`, {
        withCredentials: true,
        params: { availabilityId: availabilityId.toString() }
      })
    )) as unknown as ResourceAvailabilityDto;
  }
}
